import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TopNav,
  IconButton,
  Icon,
  icons,
  Avatar,
  EmptyState,
  Button,
  theme,
} from '@agentisai/design-system';
import { MAX_TEXT_LEN, type WorkstreamMode } from '@agentisai/shared-types';
import { useAuth } from '../auth/AuthProvider';
import { CreateWorkstreamPanel } from '../components/CreateWorkstreamPanel';
import { UserMenu } from '../components/UserMenu';
import { WorkspaceShell } from '../components/WorkspaceShell';
import { WorkspaceConversation } from '../components/WorkspaceConversation';
import { NewWorkstreamAction, WorkstreamsRailBody } from '../components/WorkstreamsRailBody';
import { useWorkstreams } from '../queries/useWorkstreams';
import { sendChatTurn, createWorkstream, sendWorkstreamChatTurn } from '../queries/client';
import { keys } from '../queries/keys';
import { describeError } from '../lib/describeError';
import { formatMessageTime } from '../lib/formatMessageTime';

/**
 * A single turn in the client-held transcript. `role` + `content` are the
 * API wire shape (posted as `messages[]` on every chat turn and on create);
 * `createdAt` is the server-authoritative RFC-3339 instant, stored RAW and
 * formatted at render (frontend-architecture R2) — never a prebaked string.
 */
type TurnEntry = {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
};

function errorMessage(err: unknown): string {
  // Friendly, specific copy per error class (validation / server / network),
  // not a blanket "Something went wrong" — see lib/describeError.
  return describeError(err).userMessage;
}

/**
 * HomePage — the protected '/' home (behind RequireAuth + RequireTenant).
 *
 * S-F1: renders the first-login EMPTY STATE for a fresh tenant, replicating
 * the `Genesis / Sample Workspaces / StratBridge_Daniel` DS story in SHAPE,
 * brand-swapped to the signed-in tenant (BG Private / Jaco for Drop 1).
 *
 * S14 wired the "+ Create First Workstream" CTA to open the modal.
 * S15 built CreateWorkstreamPanel (ChatPanel fluid + seeded intro + Composer).
 * S16 wires the two useMutations — chat turn + explicit create — so the panel
 * is a live conversational round-trip against the S05 canned-reply handlers.
 */
export function HomePage() {
  const { user, tenantBrand, getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  // Drives the main-pane empty-state copy + CTA. Only the loaded-and-empty
  // case shows "Create First Workstream"; once any workstream exists the
  // main pane prompts the user to pick one from the rail instead (opening
  // a workstream into a real workspace is deferred to a later drop).
  const workstreamsQuery = useWorkstreams();
  const hasWorkstreams = (workstreamsQuery.data?.data.length ?? 0) > 0;

  // Client-held transcript — the chat-companion model. Posted as `messages[]`
  // on every chat turn and as the final body on create. Server is stateless.
  const [transcript, setTranscript] = useState<TurnEntry[]>([]);
  const [composerValue, setComposerValue] = useState('');
  const [workstreamId, setWorkstreamId] = useState<string | null>(null);

  // Post-creation composer draft + invite-agent selection — reset per workstream.
  const [conversationValue, setConversationValue] = useState('');
  const [invitedAgent, setInvitedAgent] = useState<string | null>(null);

  // Librarian routing mode, as last reported by the chat endpoint (additive-
  // optional `data.mode`). Server-owned; this is just the latest observation.
  const [wsMode, setWsMode] = useState<WorkstreamMode>('normal');

  // Consensus-tracker conflict detail for the latest turn (additive-optional
  // `data.consensus`/`data.conflictDetail`); null once a later turn agrees.
  const [conflict, setConflict] = useState<string | null>(null);

  // The seeded intro bubble's timestamp — stamped when the panel opens (below),
  // so it reads "Just now" on open and ages with the rest of the transcript.
  const [introCreatedAt, setIntroCreatedAt] = useState(() => new Date().toISOString());

  // Coarse wall-clock tick (every 30s) fed into the render-time formatter so
  // "Just now" flips to a clock without per-second ticking. The sanctioned
  // external-system-sync effect (frontend-architecture R24/R25) — NOT
  // data-in-effect; one interval re-renders every bubble's time at once.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const sendMutation = useMutation({
    mutationKey: keys.workstream.createChat(),
    mutationFn: ({
      question,
      questionCreatedAt,
    }: {
      question: string;
      questionCreatedAt: string;
    }) => {
      const apiMessages = transcript.map(({ role, content }) => ({ role, content }));
      return sendChatTurn(
        { messages: apiMessages, question, questionCreatedAt },
        { getAccessToken },
      );
    },
    onSuccess: (data) => {
      // Render from the SERVER's authoritative timestamps (the echoed user turn
      // + the reply), not the local clock. Fall back defensively if an older
      // server omits them.
      setTranscript((prev) => [
        ...prev,
        {
          role: 'user',
          content: data.data.userMessage.content,
          createdAt: data.data.userMessage.createdAt ?? new Date().toISOString(),
        },
        {
          role: 'assistant',
          content: data.data.reply.content,
          createdAt: data.data.reply.createdAt ?? new Date().toISOString(),
        },
      ]);
      setComposerValue('');
    },
  });

  const createMutation = useMutation({
    mutationKey: keys.workstream.create(),
    mutationFn: () => {
      const apiMessages = transcript.map(({ role, content }) => ({ role, content }));
      return createWorkstream({ messages: apiMessages }, { getAccessToken });
    },
    onSuccess: (data) => {
      setTranscript((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.data.reply.content,
          // The confirmation bubble's own createdAt, else the workstream instant.
          createdAt: data.data.reply.createdAt ?? data.data.createdAt,
        },
      ]);
      setWorkstreamId(data.data.workstreamId);
      // S-C1: invalidate the rail's list so the new workstream appears without
      // a reload. void — invalidation is fire-and-forget; the next render of a
      // useWorkstreams() consumer triggers the refetch.
      void queryClient.invalidateQueries({ queryKey: keys.workstream.listMine() });
      // The conversation continues in the main pane, not the modal.
      setCreateOpen(false);
    },
  });

  // Post-creation chat turn — same client-held-transcript pattern as the
  // create flow, scoped to the now-real workstreamId + the implicit
  // 'default' focus (contract-correct; no focus persistence yet).
  const conversationMutation = useMutation({
    mutationKey: workstreamId ? keys.workstream.chat(workstreamId) : ['workstream', 'chat', 'none'],
    mutationFn: ({
      question,
      questionCreatedAt,
      action,
    }: {
      question: string;
      questionCreatedAt: string;
      action?: 'chat' | 'exitDiscovery';
    }) => {
      if (!workstreamId) throw new Error('no active workstream');
      const apiMessages = transcript.map(({ role, content }) => ({ role, content }));
      return sendWorkstreamChatTurn(
        {
          workstreamId,
          focusId: 'default',
          messages: apiMessages,
          question,
          questionCreatedAt,
          ...(invitedAgent ? { agentName: invitedAgent } : {}),
          ...(action ? { action } : {}),
        },
        { getAccessToken },
      );
    },
    onSuccess: (data) => {
      setTranscript((prev) => [
        ...prev,
        {
          role: 'user',
          content: data.data.userMessage.content,
          createdAt: data.data.userMessage.createdAt ?? new Date().toISOString(),
        },
        {
          role: 'assistant',
          content: data.data.reply.content,
          createdAt: data.data.reply.createdAt ?? new Date().toISOString(),
        },
      ]);
      // Track the Librarian's routing mode when reported (gates the invite row
      // vs the exit-discovery affordance). Absent on older/stubbed servers.
      if (data.data.mode) setWsMode(data.data.mode);
      // Surface (or clear) the consensus conflict banner for this turn.
      setConflict(
        data.data.consensus === 'conflicted'
          ? (data.data.conflictDetail ?? 'The invited agents gave conflicting answers.')
          : null,
      );
      setConversationValue('');
    },
  });

  // Map the client-held transcript → DS Message objects for the panel renderer.
  const dsMessages = transcript.map((m, i) => ({
    id: `turn-${i}`,
    memberId: m.role === 'user' ? 'user' : 'workstream-agent',
    memberName: m.role === 'user' ? (user?.name ?? 'You') : 'Workstream Agent',
    memberRole: '',
    memberType: m.role === 'user' ? ('human' as const) : ('agent' as const),
    // Workstream Agent replies use the `mira` variant (purple ring + Stamp
    // badge) — the DS canonical for the create-flow agent, matching the seeded
    // intro (CreateWorkstreamPanel) and the DS ChatPanel "Create new workstream"
    // story. NOT `agent` (blue ring, no badge), which mis-branded every reply.
    variant: m.role === 'user' ? ('human' as const) : ('mira' as const),
    text: m.content,
    time: formatMessageTime(m.createdAt, now),
  }));

  const hasUserMessage = transcript.some((m) => m.role === 'user');
  // createDisabled: no user message yet, or create is in-flight, or already created.
  const createDisabled = !hasUserMessage || createMutation.isPending || workstreamId !== null;

  const sendError = sendMutation.error ? errorMessage(sendMutation.error) : null;
  const createError = createMutation.error ? errorMessage(createMutation.error) : null;
  const conversationError = conversationMutation.error
    ? errorMessage(conversationMutation.error)
    : null;

  // Derived, not stored (R2) — looked up from the already-cached rail list;
  // falls back before the S-C1 invalidation's refetch lands post-create.
  const activeTitle =
    workstreamsQuery.data?.data.find((w) => w.workstreamId === workstreamId)?.title ?? 'Workstream';

  // firstName from the JWT `name` claim. No useMemo — the React Compiler
  // memoizes (R11/R2). trim() before split so a leading space can't yield an
  // empty first token (mirrors the DS Avatar's own initials derivation).
  const firstName = user?.name?.trim().split(/\s+/)[0] ?? 'there';
  const fullName = user?.name ?? 'User';

  // Brand tint is DATA from tenantBrand (the bgprivate map → '#1e40af'), not a
  // hardcoded style token. Undefined-safe for any not-yet-mapped tenant.
  const tint = tenantBrand?.avatarTint;
  const brandName = tenantBrand?.displayName;

  const trailing = (
    <>
      <IconButton
        icon={<Icon as={icons.Bell} />}
        aria-label="Notifications"
        size="sm"
        variant="ghost"
      />
      {/* The working profile menu / Sign Out (S-H2) — replaces the bare
          trailing avatar. The EmptyState's display avatar below is
          unchanged. UserMenu reads its own claims via useAuth(). */}
      <UserMenu />
    </>
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: theme.color.neutral.bgSurface,
      }}
    >
      <TopNav trailing={trailing} />
      <WorkspaceShell
        workstreamsPanel={
          <WorkstreamsRailBody
            onNew={() => {
              setIntroCreatedAt(new Date().toISOString());
              setCreateOpen(true);
            }}
          />
        }
        workstreamsAction={
          <NewWorkstreamAction
            onClick={() => {
              setIntroCreatedAt(new Date().toISOString());
              setCreateOpen(true);
            }}
          />
        }
      >
        {workstreamId ? (
          <WorkspaceConversation
            title={activeTitle}
            messages={dsMessages}
            value={conversationValue}
            onChange={setConversationValue}
            onSend={(q) =>
              conversationMutation.mutate({
                question: q,
                questionCreatedAt: new Date().toISOString(),
              })
            }
            sendDisabled={
              conversationMutation.isPending || conversationValue.length > MAX_TEXT_LEN || undefined
            }
            maxLength={MAX_TEXT_LEN}
            pending={conversationMutation.isPending}
            sendError={conversationError}
            invitedAgent={invitedAgent}
            onInviteAgent={setInvitedAgent}
            mode={wsMode}
            conflict={conflict}
            onExitDiscovery={() =>
              conversationMutation.mutate({
                question: 'exit discovery',
                questionCreatedAt: new Date().toISOString(),
                action: 'exitDiscovery',
              })
            }
          />
        ) : (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: theme.space[6],
            }}
          >
            <EmptyState
              icon={
                <Avatar
                  name={fullName}
                  size="display"
                  style={tint ? { background: tint, boxShadow: `0 0 0 2px ${tint}` } : undefined}
                />
              }
              title={
                <>
                  {brandName && (
                    <span
                      style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.caption,
                        fontWeight: theme.typography.fontWeight.semibold,
                        letterSpacing: theme.typography.letterSpacing.label,
                        textTransform: 'uppercase',
                        color: tint,
                        marginBottom: theme.space['1_5'],
                      }}
                    >
                      {brandName}
                    </span>
                  )}
                  Welcome to Genesis, {firstName}.
                </>
              }
              description={
                hasWorkstreams
                  ? `You're set up for ${
                      brandName ?? 'Genesis'
                    }. Select a workstream from the left to pick up where you left off.`
                  : `You're set up for ${
                      brandName ?? 'Genesis'
                    }. When you create your first workstream, it'll appear here. Or one of your colleagues will share theirs with you.`
              }
              action={
                // Show the "Create First Workstream" CTA only when we KNOW the
                // list is empty — hide it while loading (avoid a CTA flash) and
                // once any workstream exists (the rail + `+ New` action are the
                // create affordance from then on).
                workstreamsQuery.isSuccess && !hasWorkstreams ? (
                  // Matches the StratBridge_Daniel story's `EmptyStateCta` exactly:
                  // ghostStrong + 10x24 padding, body-coloured text, fainter border,
                  // 13px / 600 (bodySm / semibold) — not the default Button metrics.
                  <Button
                    variant="ghostStrong"
                    style={{
                      padding: '10px 24px',
                      color: theme.color.neutral.body,
                      borderColor: theme.color.neutral.fainter,
                      fontSize: theme.typography.fontSize.bodySm,
                      fontWeight: theme.typography.fontWeight.semibold,
                    }}
                    onClick={() => {
                      // Stamp the intro at open so it reads "Just now" then ages.
                      setIntroCreatedAt(new Date().toISOString());
                      setCreateOpen(true);
                    }}
                  >
                    + Create First Workstream
                  </Button>
                ) : undefined
              }
            />
          </div>
        )}
      </WorkspaceShell>
      <CreateWorkstreamPanel
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        messages={dsMessages}
        introTime={formatMessageTime(introCreatedAt, now)}
        value={composerValue}
        onChange={setComposerValue}
        onSend={(q) =>
          sendMutation.mutate({ question: q, questionCreatedAt: new Date().toISOString() })
        }
        // Block send while a turn is in flight OR the draft exceeds the shared
        // contract limit (MAX_TEXT_LEN) — the panel shows inline feedback so the
        // user knows BEFORE clicking, instead of a post-send server rejection.
        // `|| undefined` so the Composer's own empty-draft guard still applies.
        sendDisabled={sendMutation.isPending || composerValue.length > MAX_TEXT_LEN || undefined}
        maxLength={MAX_TEXT_LEN}
        pending={sendMutation.isPending}
        onCreate={() => createMutation.mutate()}
        createDisabled={createDisabled}
        creating={createMutation.isPending}
        sendError={sendError}
        createError={createError}
      />
    </div>
  );
}
HomePage.displayName = 'HomePage';
